import type { VoidFunctionComponent } from 'react';
import { useSelector } from 'react-redux';

import * as PackageInventoryStore from '../../../redux/packageInventory';
import { SearchInput } from '../../generic/Forms';
import useFetchPackages from '../hooks/useFetchPackages';
import usePackageManagers from '../hooks/usePackageManagers';
import type { WithResetSelectedPackage } from '../PackageInventoryContainer';
import type { Fetcher } from '../../../../shared/organizationPackages';
import type { SelectOption } from '../../../types';
import PackageSortingDropdown from '../PackageSortingDropdown';
import Select from '../../generic/Select/Select';
import styles from './PackageFilters.module.less';
import PackageLocatorFilter from './PackageLocatorFilter';

type Props = {
  withResetSelectedPackage: WithResetSelectedPackage;
};

/**
 * PackageInventoryFiltersPackage has filters related to searching
 * by package name or by a specific package manager.
 */
const PackageInventoryFiltersPackage: VoidFunctionComponent<Props> = ({ withResetSelectedPackage }) => {
  const setFilters = useFetchPackages(PackageInventoryStore.actions.setFilters);
  const packageName = useSelector(PackageInventoryStore.selectors.getFiltersPackageName);
  const selectedFetchers = useSelector(PackageInventoryStore.selectors.getFiltersFetchersOptions);
  const selectedLocators = useSelector(PackageInventoryStore.selectors.getFiltersLocatorsOptions);
  const { fetchers: fetcherOptions, isLoading } = usePackageManagers();
  return (
    <div className={styles.filters}>
      <SearchInput
        label="package-search"
        size="small"
        placeholder="Search by package name"
        value={packageName}
        onChange={withResetSelectedPackage((name) => setFilters({ packageName: name }))}
        testId="package-inventory-searchbar"
      />
      <div data-test-id="package-inventory-filters--fetchers">
        <Select
          isMulti
          isClearable
          id="Package manager"
          placeholder={isLoading ? 'Loading...' : 'Package manager'}
          value={selectedFetchers}
          options={fetcherOptions}
          isDisabled={isLoading}
          onChange={withResetSelectedPackage((fetchers) =>
            setFilters({ fetchers: (fetchers as Array<SelectOption<Fetcher>>).map(({ value }) => value) }),
          )}
        />
      </div>
      <PackageLocatorFilter locators={selectedLocators} setFilters={withResetSelectedPackage(setFilters)} />
      <PackageSortingDropdown withResetSelectedPackage={withResetSelectedPackage} />
    </div>
  );
};

export default PackageInventoryFiltersPackage;
